// src/AnimationView/engine/GameEngine.ts
/**
 * Top-level wrapper around Phaser.Game that mounts our PhaserScene,
 * exposes batch updaters (handleCharacters/Objects/Texts), scene flags,
 * and lifecycle controls (reset/stop).
 *
 * Notes for live updates:
 * - The engine is created once per "boot". React effects should not reboot it
 *   when characters/objects/texts change — those go through the handle* APIs.
 */

import * as React from "react";
import Phaser from "phaser";

import { PhaserScene } from "./PhaserScene";
import { EventManager } from "./events/EventManager";

import type {
  CharacterConfig,
  ObjectConfig,
  PhaserConfig,
  SceneConfig,
  TextObjectConfig,
} from "../types/config";
import type { TKeyValueDataString } from "../../../models/objects/gen";

// Types for convenience (returned from getters)
import type { Character } from "./entities/Character";
import type { GameObject } from "./entities/Object";
import type { TextObject } from "./entities/Text";

export interface EngineConfig {
  eventManager?: EventManager | null;
  PhaserConfig: PhaserConfig;
  assets: TKeyValueDataString[]; // image/audio keys to preload
}

function canUseWebGL(): boolean {
  if (typeof document === "undefined") return true;
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl2") ||
      canvas.getContext("webgl") ||
      canvas.getContext("experimental-webgl");
    return !!gl;
  } catch {
    return false;
  }
}

export class GameEngine {
  private adminMode: boolean;
  private game?: Phaser.Game;
  public phaserScene?: PhaserScene;
  private detachWinListener?: () => void;

  // optional: run a callback when the game reports READY
  private onSceneReady?: () => void;

  constructor(
    adminMode: boolean,
    config: EngineConfig,
    containerRef: React.MutableRefObject<HTMLElement | null>,
    onWin: () => void
  ) {
    this.adminMode = adminMode;

    // Build our scene with the provided config
    this.phaserScene = new PhaserScene(
      this.adminMode,
      {
        PhaserConfig: config.PhaserConfig,
        eventManager: config.eventManager ?? null,
        assets: config.assets,
      },
      onWin
    );

    // Relayer l'événement "win" de l'EventManager vers la scène
    const em = this.getEventManager?.();
    if (em) {
      const onWinEvt = () => this.phaserScene?.triggerWin?.();
      em.on?.("win", onWinEvt);

      // fallback si em.off n'existe pas
      this.detachWinListener = () => {
        if (em.off) em.off("win", onWinEvt);
        else {
          const arr = (em as any)?.genericListeners?.get?.("win");
          if (arr && Array.isArray(arr)) {
            const i = arr.indexOf(onWinEvt);
            if (i !== -1) arr.splice(i, 1);
          }
        }
      };
    }

    // If the React ref is not yet set, Phaser will mount to <body>.
    // Prefer passing undefined instead of null to satisfy Phaser types.
    const parentEl = containerRef?.current;
    if (!parentEl) {
      throw new Error('[GameEngine] containerRef.current is null (Phaser would mount to <body>)');
    }

    const parent: string | HTMLElement = parentEl;

    const preferredType = canUseWebGL() ? Phaser.WEBGL : Phaser.CANVAS;

    const phaserConfigBase: Omit<Phaser.Types.Core.GameConfig, "type"> = {
      width: Number(config.PhaserConfig.scene.width) || 400,
      height: Number(config.PhaserConfig.scene.height) || 400,
      parent,
      scene: [this.phaserScene],
      transparent: true,
      // FIT laisse Phaser adapter la taille CSS du canvas a celle du parent en
      // conservant le ratio. La resolution interne ne bouge pas (les entites
      // continuent de lire sys.canvas.width), et le ScaleManager corrige les
      // coordonnees de pointeur : indispensable sur mobile ou la scene 400x400
      // debordait de l'ecran.
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
      physics: {
        default: "arcade",
        arcade: {
          debug: this.adminMode,
          gravity: { x: 0, y: 0 }, // top-down by default; use setGravity for per-scene override
        },
      },
      // You can enable pixelArt or roundPixels if needed:
      // pixelArt: true,
      // render: { roundPixels: true },
    };
    try {
      this.game = new Phaser.Game({ ...phaserConfigBase, type: preferredType });
    } catch (e) {
      console.warn("[GameEngine] Phaser init failed, retrying with CANVAS", e);
      this.game = new Phaser.Game({ ...phaserConfigBase, type: Phaser.CANVAS });
    }

    // Notify when Phaser boots fully
    this.game.events.once(Phaser.Core.Events.READY, () => {
      try {
        this.onSceneReady?.();
      } catch {
        /* no-op */
      }
    });
  }

  // ───────────────────────────────── Lifecycle ─────────────────────────────────

  /**
   * Optionally provide a callback that fires when the game reports READY.
   * (You can ignore this if you don't need it.)
   */
  public start(onReady?: () => void) {
    this.onSceneReady = onReady;
  }

  /**
   * Recalcule la taille CSS du canvas.
   *
   * Scale.FIT n'ecoute que le resize de la fenetre : quand c'est le conteneur
   * qui change (menu lateral, colonne qui s'elargit), il faut le lui dire.
   */
  public refreshScale() {
    try {
      this.game?.scale?.refresh();
    } catch {
      /* le jeu n'est pas encore pret */
    }
  }

  /** Reset the scene state without destroying the Phaser.Game */
  public reset() {
    this.phaserScene?.reset();
  }

  /** Cleanly destroy the Phaser.Game and clear references */
  public stop() {
    // Let the scene clean itself first
    try {
      this.phaserScene?.cleanup();
    } catch {
      /* no-op */
    }

    // Destroy Phaser
    try {
      this.game?.destroy(true);
    } finally {
      this.game = undefined;
      this.phaserScene = undefined;
      this.onSceneReady = undefined;
    }

    try { this.detachWinListener?.(); } catch { }
    this.detachWinListener = undefined;
  }

  // ───────────────────────────── Batch updaters (React→Scene) ─────────────────────────────

  public handleCharacters(characters: CharacterConfig[]) {
    this.phaserScene?.handleCharacters(characters);
  }

  public handleObjects(objects: ObjectConfig[]) {
    this.phaserScene?.handleObjects(objects);
  }

  public handleTexts(texts: TextObjectConfig[]) {
    this.phaserScene?.handleTexts(texts);
  }

  // ───────────────────────────── Scene-wide settings ─────────────────────────────

  public updateSceneBackground(scene: SceneConfig) {
    this.phaserScene?.updateSceneBackground(scene);
  }

  public setGravity(active: boolean, y?: number) {
    this.phaserScene?.setGravity(active, y);
  }

  // ───────────────────────────── Object flags (forwarders) ─────────────────────────────

  public setObjectPickable(objectId: string, order = 0) {
    this.phaserScene?.setObjectPickable(objectId, order);
  }
  public clearObjectPickable(objectId: string) {
    this.phaserScene?.clearObjectPickable(objectId);
  }

  public setObjectImmovable(objectId: string) {
    this.phaserScene?.setObjectImmovable(objectId);
  }
  public clearObjectImmovable(objectId: string) {
    this.phaserScene?.clearObjectImmovable(objectId);
  }

  public setObjectPushable(objectId: string) {
    this.phaserScene?.setObjectPushable(objectId);
  }
  public clearObjectPushable(objectId: string) {
    this.phaserScene?.clearObjectPushable(objectId);
  }

  public setObjectDecor(objectId: string) {
    this.phaserScene?.setObjectDecor(objectId);
  }
  public clearObjectDecor(objectId: string) {
    this.phaserScene?.clearObjectDecor(objectId);
  }

  // ───────────────────────────── Getters / helpers ─────────────────────────────

  public getEventManager(): EventManager | null | undefined {
    return this.phaserScene?.getEventManager();
  }

  public getCharacter(id: string): Character | undefined {
    if (!id) return this.phaserScene?.getCharacterFirst();
    return this.phaserScene?.getCharacterById(id);
  }

  public getObject(id: string | number): GameObject | undefined {
    return this.phaserScene?.getObjectById(String(id));
  }

  public getTextObject(id: string): TextObject | undefined {
    return this.phaserScene?.getTextObjectById(id);
  }

  public isObjectPicked(id: string | number): boolean {
    return this.phaserScene?.isObjectPicked(String(id)) ?? false;
  }

  public areAllPickablesPicked(): boolean {
    return this.phaserScene?.areAllPickablesPicked() ?? false;
  }

  // Optional convenience getters (if you expose them on the scene)
  public getAllCharacterIds(): string[] {
    return (
      (this.phaserScene as any)?.getAllCharacterIds?.() ?? []
    );
  }
  public getAllObjectIds(): string[] {
    return (this.phaserScene as any)?.getAllObjectIds?.() ?? [];
  }
  public getAllTextIds(): string[] {
    return (this.phaserScene as any)?.getAllTextIds?.() ?? [];
  }

  public getAudioManager() {
    return (this.phaserScene as any)?.getAudioManager?.();
  }

  public win(): void {
    // Chemin canonique : la scène gère animations + callback React (onWin)
    if (this.phaserScene?.triggerWin) {
      this.phaserScene.triggerWin();
      return;
    }
    // Fallback : on relaie via l'EventManager si la scène ne l'expose pas
    this.getEventManager()?.emit("win");
  }

  setGridEnabled(enabled: boolean) {
    this.phaserScene?.setGridEnabled?.(enabled);
  }

}
